import { useMemo, useState } from 'react';
import { DeleteOutlined, EditOutlined, FolderAddOutlined, PlusOutlined, WarningOutlined } from '@ant-design/icons';

import { MenuThaoTac } from '../../../../components/shared';
import { Badge, Button, Dialog, Empty, Field, Input, Skeleton } from '../../../../components/ui';
import { useModulePermissions } from '../../../../hooks/usePermission';
import { useT } from '../../../../i18n';
import { cn } from '../../../../lib/cn';
import { soVN } from '../../../../lib/dinh-dang';
import { layTuChoi } from '../../../../lib/repositories/tro-ly-ai.repository';
import { useToast } from '../../../../lib/toast';
import { QUYEN } from '../../constants';
import { useDanhSachKhuon, useDoiChieuCheDo, useThaoTacKhuon } from '../../hooks/useKhuon';
import { useMoTaLoi } from '../../hooks/useMoTaLoi';
import { NHAP_RONG, bangThieuTuTuChoi, thanLuuKhuon, tuKhuon, type Nhap } from '../../nghiep-vu/khuon-nhap';
import type { CheDoKhai, DoiChieuCheDo, KetQuaChayThu, Khuon, NhomKhuon } from '../../types';
import { DaiLoi } from '../chung/VungDuLieu';
import { HopXacNhan } from '../chung/HopXacNhan';
import { KHOI, SoanKhuon } from './SoanKhuon';

/**
 * Khuôn nghiệp vụ — cách khai THỨ HAI, và cái công tắc giữa hai cách (port `components/replica/KhuonTab.tsx` native).
 *
 * Cách khai bằng câu hỏi hỏi về MỘT Ô TRỪU TƯỢNG mỗi câu — đo trên SAP thật là 325 câu. Màn này đổi ĐƠN VỊ CÔNG VIỆC:
 * người nghiệp vụ viết câu truy vấn quen thuộc, và câu ấy nói ra bằng hành động những điều phỏng vấn hỏi bằng lời
 * (`SUM(thanh_tien)` là cột cộng dồn được).
 *
 * Ba vùng: trái cây nhóm → khuôn (kèm dấu "đã vào bộ chọn chưa"); giữa trình soạn; phải PHẠM VI — chỗ người soạn thấy
 * hậu quả câu lệnh vừa viết. Chạy thử là bước BẮT BUỘC: khuôn chưa từng chạy ra dòng là một lời hứa, nền tảng không cho
 * vào bộ chọn — nên "chưa chạy thử" phải đọc được ngay từ cây bên trái.
 */
export interface KhuonTabProps {
  sourceId: string;
  /** Nguồn API: mọi khuôn là khuôn API, và nguồn luôn trả lời theo khuôn. */
  laApi?: boolean;
  diaChiGoc?: string;
}

export function KhuonTab({ sourceId, laApi = false, diaChiGoc = '' }: KhuonTabProps) {
  const { t } = useT();
  const toast = useToast();
  const moTaLoi = useMoTaLoi();
  const q = useModulePermissions(QUYEN);
  const ds = useDanhSachKhuon(sourceId);
  const tt = useThaoTacKhuon(sourceId);

  const [dangChon, setDangChon] = useState<string | null>(null);
  const [nhap, setNhap] = useState<Nhap | null>(null);
  const [ketQua, setKetQua] = useState<KetQuaChayThu | null>(null);
  // Hộp nhóm: `undefined` = đóng, `null` = đang THÊM, còn lại = nhóm đang đổi tên.
  const [hopNhom, setHopNhom] = useState<NhomKhuon | null | undefined>(undefined);
  const [tenNhom, setTenNhom] = useState('');
  const [nhomXoa, setNhomXoa] = useState<NhomKhuon | null>(null);
  const [moXoaKhuon, setMoXoaKhuon] = useState(false);
  const [moDoiChieu, setMoDoiChieu] = useState(false);
  // Bảng mà lần lưu vừa rồi nói là bản khai chưa có. Giữ ở state chứ không suy lại từ lỗi của mutation: khai lại xong
  // thì lỗi cũ vẫn còn trong mutation, và khối nhắc sẽ không chịu biến đi.
  const [banKhaiThieu, setBanKhaiThieu] = useState<string[]>([]);
  // Tham số cho lượt chạy thử. Để rỗng là chạy trên toàn bộ dữ liệu.
  const [dienThu, setDienThu] = useState<Record<string, string>>({});

  const doiChieu = useDoiChieuCheDo(sourceId, moDoiChieu);

  const nhom = ds.data?.nhom ?? [];
  const khuon = useMemo(() => ds.data?.khuon ?? [], [ds.data]);
  const cheDo = ds.data?.cheDo ?? 'cau_hoi';
  const dangSua = useMemo(() => khuon.find((k) => k.id === dangChon) ?? null, [khuon, dangChon]);

  const moHopNhom = (n: NhomKhuon | null) => {
    setHopNhom(n);
    setTenNhom(n?.ten ?? '');
    tt.luuNhom.reset();
  };

  const luuNhom = () =>
    tt.luuNhom.mutate(
      { nhom: hopNhom ?? null, ten: tenNhom.trim() },
      {
        onSuccess: () => {
          setHopNhom(undefined);
          toast.success(t('troLyAiKhuon.daLuuNhom'));
        },
      },
    );

  const xoaNhom = () => {
    if (!nhomXoa) return;
    tt.xoaNhom.mutate(nhomXoa.id, {
      onSuccess: () => {
        setNhomXoa(null);
        toast.success(t('troLyAiKhuon.daXoaNhom'));
      },
    });
  };

  const luu = (n: Nhap) =>
    tt.luu.mutate(thanLuuKhuon(n), {
      onSuccess: (k) => {
        setDangChon(k.id);
        setNhap(tuKhuon(k));
        setKetQua(null);
        setBanKhaiThieu([]);
        // Nói ra bước còn thiếu ngay tại chỗ: "đã lưu" một mình làm người soạn tưởng khuôn đã dùng được.
        toast.success(k.vaoBoChon ? t('troLyAiKhuon.daLuu') : t('troLyAiKhuon.daLuuChuaChayThu'));
      },
      onError: (e) => {
        setBanKhaiThieu(bangThieuTuTuChoi(layTuChoi(e)));
        toast.error(moTaLoi(e));
      },
    });

  /**
   * Khai lại ĐÚNG những bảng đang thiếu rồi lưu lại khuôn ngay. Bảo người soạn rời màn, sang tab khác, tìm đúng bảng
   * giữa vài nghìn cái, nhớ tick "giữ lời khai", rồi quay lại dán lại — là sáu bước cho một việc máy biết đủ để tự làm.
   */
  const capNhatBanKhai = () =>
    tt.capNhatBanKhai.mutate(banKhaiThieu, {
      onSuccess: (so) => {
        if (!so) {
          toast.error(t('troLyAiKhuon.khongThayBangONguon', { bang: banKhaiThieu.join(', ') }));
          return;
        }
        setBanKhaiThieu([]);
        toast.success(t('troLyAiKhuon.daCapNhatBanKhai'));
        if (nhap) luu(nhap);
      },
      onError: (e) => toast.error(moTaLoi(e)),
    });

  const xoaKhuon = () => {
    if (!dangSua) return;
    tt.xoa.mutate(dangSua.id, {
      onSuccess: () => {
        setMoXoaKhuon(false);
        setDangChon(null);
        setNhap(null);
        setKetQua(null);
        toast.success(t('troLyAiKhuon.daXoa'));
      },
    });
  };

  const chayThu = () => {
    if (!dangSua) return;
    tt.chayThu.mutate(
      { id: dangSua.id, thamSo: dienThu },
      {
        onSuccess: (kq) => {
          setKetQua(kq);
          toast.success(
            kq.rowCount ? t('troLyAiKhuon.chayThuXong', { n: kq.rowCount }) : t('troLyAiKhuon.chayThuKhongDong'),
          );
        },
        onError: (e) => toast.error(moTaLoi(e)),
      },
    );
  };

  const datCheDo = (c: CheDoKhai) =>
    tt.datCheDo.mutate(c, {
      onSuccess: () => {
        setMoDoiChieu(false);
        toast.success(t('troLyAiKhuon.daDoiCheDo'));
      },
    });

  const chon = (k: Khuon) => {
    setDangChon(k.id);
    setNhap(tuKhuon(k));
    setKetQua(null);
  };

  const themKhuon = (nhomId: string) => {
    setDangChon(null);
    setNhap(NHAP_RONG(nhomId, laApi ? 'api' : 'cau_lenh'));
    setKetQua(null);
  };

  if (ds.isPending) return <Skeleton className="h-64 w-full" />;
  if (ds.error) return <DaiLoi loi={ds.error} onThuLai={() => void ds.refetch()} />;

  const duocLuuKhuon = nhap?.id ? q.nguonSua : q.nguonTao;

  return (
    <div className="flex flex-col gap-4">
      {laApi ? null : (
        <ThanhCheDo
          cheDo={cheDo}
          soSanSang={khuon.filter((k) => k.vaoBoChon).length}
          soKhuon={khuon.length}
          duocDoi={q.nguonSua}
          onDoiChieu={() => {
            tt.datCheDo.reset();
            setMoDoiChieu(true);
          }}
        />
      )}

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,17rem)_minmax(0,1fr)]">
        {/* Trái: cây nhóm → khuôn */}
        <nav aria-label={t('troLyAiKhuon.nhom')} className={cn(KHOI, 'p-3')}>
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <span className="text-sm font-medium text-content-primary">{t('troLyAiKhuon.nhom')}</span>
            {q.nguonTao && (
              <Button size="sm" variant="outline" icon={<FolderAddOutlined />} onClick={() => moHopNhom(null)}>
                {t('troLyAiKhuon.themNhom')}
              </Button>
            )}
          </div>
          {nhom.length === 0 ? (
            <Empty title={t('troLyAiKhuon.chuaCoNhom')} />
          ) : (
            <div className="flex flex-col gap-3">
              {nhom.map((n) => {
                const cua = khuon.filter((k) => k.nhomId === n.id);
                return (
                  <div key={n.id}>
                    <div className="flex items-center justify-between gap-1">
                      <span className="min-w-0 truncate text-sm font-medium text-content-primary" title={n.ten}>
                        {n.ten}
                      </span>
                      <div className="flex shrink-0 items-center gap-1">
                        {q.nguonTao && (
                          <Button
                            size="sm"
                            variant="ghost"
                            icon={<PlusOutlined />}
                            title={t('troLyAiKhuon.themKhuon')}
                            aria-label={t('troLyAiKhuon.themKhuon')}
                            onClick={() => themKhuon(n.id)}
                          />
                        )}
                        <MenuThaoTac
                          tenBanGhi={n.ten}
                          muc={[
                            {
                              khoa: 'doi-ten',
                              nhan: t('troLyAiKhuon.doiTenNhom'),
                              icon: <EditOutlined />,
                              hien: q.nguonSua,
                              onChon: () => moHopNhom(n),
                            },
                            {
                              khoa: 'xoa',
                              nhan: t('troLyAiKhuon.xoaNhom'),
                              icon: <DeleteOutlined />,
                              nhom: 'pha-huy',
                              hien: q.nguonXoa,
                              onChon: () => {
                                tt.xoaNhom.reset();
                                setNhomXoa(n);
                              },
                            },
                          ]}
                        />
                      </div>
                    </div>
                    <ul className="mt-1 flex flex-col">
                      {cua.map((k) => (
                        <li key={k.id}>
                          <button
                            type="button"
                            onClick={() => chon(k)}
                            aria-current={k.id === dangChon ? 'true' : undefined}
                            className={cn(
                              'flex w-full items-center gap-2 rounded px-2 py-1.5 text-start text-sm text-content-primary hover:bg-subtle',
                              k.id === dangChon && 'bg-primary-50 font-medium text-primary-700',
                            )}
                          >
                            <span
                              role="img"
                              aria-label={k.vaoBoChon ? t('troLyAiKhuon.daSanSang') : t('troLyAiKhuon.chuaSanSang')}
                              title={k.vaoBoChon ? t('troLyAiKhuon.daSanSang') : t('troLyAiKhuon.chuaSanSang')}
                              className={cn(
                                'size-1.5 shrink-0 rounded-full',
                                k.vaoBoChon ? 'bg-success-600' : 'bg-warning-500',
                              )}
                            />
                            <span className="truncate">{k.ten}</span>
                          </button>
                        </li>
                      ))}
                      {cua.length === 0 && (
                        <li className="px-2 text-xs text-content-muted">{t('troLyAiKhuon.nhomRong')}</li>
                      )}
                    </ul>
                  </div>
                );
              })}
            </div>
          )}
        </nav>

        {/* Giữa + phải: soạn khuôn và phạm vi */}
        {nhap === null ? (
          <div className={cn(KHOI, 'p-6')}>
            <Empty title={nhom.length ? t('troLyAiKhuon.chonMotKhuon') : t('troLyAiKhuon.batDauBangNhom')} />
          </div>
        ) : (
          <SoanKhuon
            nhap={nhap}
            setNhap={setNhap}
            nhom={nhom}
            khuon={dangSua}
            dialect={ds.data?.dialect ?? 'postgres'}
            laApi={laApi}
            diaChiGoc={diaChiGoc}
            ketQua={ketQua}
            dangLuu={tt.luu.isPending}
            dangChay={tt.chayThu.isPending}
            dangXoa={tt.xoa.isPending}
            banKhaiThieu={banKhaiThieu}
            dangCapNhatBanKhai={tt.capNhatBanKhai.isPending}
            onCapNhatBanKhai={capNhatBanKhai}
            dienThu={dienThu}
            onDien={setDienThu}
            onLuu={() => luu(nhap)}
            onChayThu={chayThu}
            onXoa={() => {
              tt.xoa.reset();
              setMoXoaKhuon(true);
            }}
            quyen={{ luu: duocLuuKhuon, xoa: q.nguonXoa, chayThu: q.nguonSua }}
          />
        )}
      </div>

      <Dialog
        open={hopNhom !== undefined}
        onClose={() => setHopNhom(undefined)}
        title={hopNhom ? t('troLyAiKhuon.doiTenNhom') : t('troLyAiKhuon.themNhom')}
      >
        <form
          className="flex flex-col gap-3 pt-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (tenNhom.trim() && tenNhom.trim() !== hopNhom?.ten) luuNhom();
          }}
        >
          <Field label={t('troLyAiKhuon.tenNhom')} required error={tt.luuNhom.error ? moTaLoi(tt.luuNhom.error) : undefined}>
            {(p) => (
              <Input
                {...p}
                autoFocus
                value={tenNhom}
                onChange={(e) => setTenNhom(e.target.value)}
                placeholder={t('troLyAiKhuon.tenNhomPlaceholder')}
              />
            )}
          </Field>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setHopNhom(undefined)}>
              {t('chung.huy')}
            </Button>
            <Button
              variant="primary"
              htmlType="submit"
              disabled={!tenNhom.trim() || tenNhom.trim() === hopNhom?.ten}
              loading={tt.luuNhom.isPending}
            >
              {t('troLyAiKhuon.luu')}
            </Button>
          </div>
        </form>
      </Dialog>

      <HopXacNhan
        open={nhomXoa !== null}
        onClose={() => setNhomXoa(null)}
        tieuDe={t('troLyAiKhuon.xoaNhomXacNhan', { ten: nhomXoa?.ten ?? '' })}
        than={t('troLyAiKhuon.xoaNhomXacNhanMoTa')}
        nhanXacNhan={t('troLyAiKhuon.xoaNhom')}
        dangChay={tt.xoaNhom.isPending}
        loi={tt.xoaNhom.error}
        onXacNhan={xoaNhom}
      />

      <HopXacNhan
        open={moXoaKhuon}
        onClose={() => setMoXoaKhuon(false)}
        tieuDe={t('troLyAiKhuon.xoaXacNhan')}
        than={t('troLyAiKhuon.xoaXacNhanMoTa')}
        nhanXacNhan={t('troLyAiKhuon.xoa')}
        dangChay={tt.xoa.isPending}
        loi={tt.xoa.error}
        onXacNhan={xoaKhuon}
      />

      <HopDoiChieu
        open={moDoiChieu}
        onClose={() => setMoDoiChieu(false)}
        data={doiChieu.data}
        dangTai={doiChieu.isPending}
        loiTai={doiChieu.error}
        loiGat={tt.datCheDo.error}
        dangGat={tt.datCheDo.isPending}
        onGat={datCheDo}
      />
    </div>
  );
}

/**
 * Thanh chế độ: nói ra chế độ đang bật VÀ con số quyết định. "12 khuôn, 9 đã chạy thử" là thứ người vận hành cần trước
 * khi nghĩ tới việc gạt — ba khuôn còn lại sẽ không trả lời câu nào.
 */
function ThanhCheDo({
  cheDo, soSanSang, soKhuon, duocDoi, onDoiChieu,
}: {
  cheDo: CheDoKhai;
  soSanSang: number;
  soKhuon: number;
  duocDoi: boolean;
  onDoiChieu: () => void;
}) {
  const { t, locale } = useT();
  const dangKhuon = cheDo === 'khuon';
  return (
    <div className={cn(KHOI, 'flex flex-wrap items-center justify-between gap-3 px-4 py-3')}>
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant={dangKhuon ? 'success' : 'neutral'}>
          {dangKhuon ? t('troLyAiKhuon.dangBatKhuon') : t('troLyAiKhuon.dangBatCauHoi')}
        </Badge>
        <span className="text-sm text-content-muted">
          {t('troLyAiKhuon.tomTatSanSang', { san: soVN(soSanSang, locale), tong: soVN(soKhuon, locale) })}
        </span>
      </div>
      {duocDoi && (
        <Button variant="outline" onClick={onDoiChieu}>
          {dangKhuon ? t('troLyAiKhuon.veCauHoi') : t('troLyAiKhuon.sangKhuon')}
        </Button>
      )}
    </div>
  );
}

/**
 * Đối chiếu trước khi gạt công tắc. Gạt là đổi TOÀN BỘ cơ sở quyết định ai đọc được gì; cái người bấm KHÔNG biết là vai
 * nào sẽ hết hỏi được và khuôn nào chưa chạy thử — nên hộp thoại liệt kê đúng hai thứ ấy.
 */
function HopDoiChieu({
  open, onClose, data, dangTai, loiTai, loiGat, dangGat, onGat,
}: {
  open: boolean;
  onClose: () => void;
  data?: DoiChieuCheDo;
  dangTai: boolean;
  loiTai: unknown;
  loiGat: unknown;
  dangGat: boolean;
  onGat: (c: CheDoKhai) => void;
}) {
  const { t, locale } = useT();
  const moTaLoi = useMoTaLoi();
  const dich: CheDoKhai = data?.cheDo === 'khuon' ? 'cau_hoi' : 'khuon';
  return (
    <Dialog open={open} onClose={onClose} title={t('troLyAiKhuon.doiChieuTieuDe')} width={640}>
      {loiTai ? (
        <DaiLoi loi={loiTai} />
      ) : dangTai || !data ? (
        <Skeleton className="h-40 w-full" />
      ) : (
        <div className="flex flex-col gap-3 pt-2">
          <p className="text-sm text-content-secondary">
            {dich === 'khuon' ? t('troLyAiKhuon.gatSangKhuonNghia') : t('troLyAiKhuon.gatVeCauHoiNghia')}
          </p>

          <div className="grid grid-cols-2 gap-2 text-sm">
            <div className="rounded-lg border border-default px-3 py-2">
              <p className="text-xs text-content-muted">{t('troLyAiKhuon.soKhuon')}</p>
              <p className="text-lg font-medium tabular-nums">{soVN(data.soKhuon, locale)}</p>
            </div>
            <div className="rounded-lg border border-default px-3 py-2">
              <p className="text-xs text-content-muted">{t('troLyAiKhuon.soKhuonSanSang')}</p>
              <p className="text-lg font-medium tabular-nums">{soVN(data.soKhuonSanSang, locale)}</p>
            </div>
          </div>

          {dich === 'khuon' && data.vaiKhongDungDuocKhuonNao.length > 0 && (
            <div className="rounded-lg border border-warning-100 bg-warning-50 px-3 py-2">
              <p className="text-sm font-medium text-warning-700">
                <WarningOutlined /> {t('troLyAiKhuon.vaiSeHetHoiDuoc')}
              </p>
              <p className="mt-1 text-xs text-warning-700">{data.vaiKhongDungDuocKhuonNao.join(', ')}</p>
              <p className="mt-1 text-xs text-warning-700">{t('troLyAiKhuon.vaiSeHetHoiDuocViSao')}</p>
            </div>
          )}

          {data.khuonChuaChayThu.length > 0 && (
            <div className="rounded-lg border border-default px-3 py-2">
              <p className="text-sm font-medium text-content-primary">{t('troLyAiKhuon.khuonChuaChayThu')}</p>
              <p className="mt-1 text-xs text-content-muted">{data.khuonChuaChayThu.map((k) => k.ten).join(', ')}</p>
            </div>
          )}

          {data.khuonBiHa.length > 0 && (
            <div className="rounded-lg border border-danger-100 px-3 py-2">
              <p className="text-sm font-medium text-danger-700">{t('troLyAiKhuon.khuonBiHa')}</p>
              {data.khuonBiHa.map((k) => (
                <p key={k.id} className="mt-1 text-xs text-danger-700">
                  {k.ten} — {k.lyDo}
                </p>
              ))}
            </div>
          )}

          {loiGat ? (
            <p role="alert" className="rounded border border-danger-100 bg-danger-50 p-3 text-sm text-danger-700">
              {moTaLoi(loiGat)}
            </p>
          ) : null}

          <div className="flex flex-wrap justify-end gap-2">
            <Button variant="outline" onClick={onClose}>
              {t('chung.huy')}
            </Button>
            <Button variant="primary" loading={dangGat} onClick={() => onGat(dich)}>
              {dich === 'khuon' ? t('troLyAiKhuon.sangKhuon') : t('troLyAiKhuon.veCauHoi')}
            </Button>
          </div>
        </div>
      )}
    </Dialog>
  );
}
